import React from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import {RiCheckLine, RiCloseLine, RiFileCopyLine} from "react-icons/ri";
import {useGroupManager} from "../../Providers/groupManager";
import {inviteOperator} from "../../../Services/operatorFunctions";
import {
    Backdrop, Card, Header, HeadText, Title, Hint, CloseBtn, Body, Label, Input,
    Tabs, Tab, RoleDesc, KeyBox, IconBtn, SubHint, AccessTitle, Checklist, CheckItem,
    Warn, Success, Footer, FooterNote, Actions, CancelBtn, SendBtn,
} from "./styles";

const ROLES = ["admin", "operator", "viewer"];

const ACCESS = {
    admin: {inbox: true, group_settings: true, members: true, operator_access: true, view: true},
    operator: {inbox: true, group_settings: false, members: false, operator_access: false, view: true},
    viewer: {inbox: false, group_settings: false, members: false, operator_access: false, view: true},
};
const ACCESS_ITEMS = ["inbox", "group_settings", "members", "operator_access", "view"];

const KEY_ALPHABET = "ABCDEFGHJKMNPQRSTVWXYZ23456789";

const randomBytes = (n) => {
    const c = typeof window !== "undefined" && (window.crypto || window.msCrypto);
    if (c && c.getRandomValues) return Array.from(c.getRandomValues(new Uint32Array(n)));
    return Array.from({length: n}, () => Math.floor(Math.random() * 0xffffffff));
};

const generateKey = () => {
    const idx = randomBytes(12);
    let out = "";
    for (let i = 0; i < 12; i++) {
        if (i > 0 && i % 4 === 0) out += "-";
        out += KEY_ALPHABET[idx[i] % KEY_ALPHABET.length];
    }
    return out;
};

const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

const OperatorInviteModal = ({open, campaignId, onCancel, onDone}) => {
    const {t} = useTranslation();
    const {campaign} = useGroupManager();
    const [fullName, setFullName] = React.useState("");
    const [email, setEmail] = React.useState("");
    const [role, setRole] = React.useState("operator");
    const [accessKey, setAccessKey] = React.useState("");
    const [submitting, setSubmitting] = React.useState(false);
    const [result, setResult] = React.useState(null);

    React.useEffect(() => {
        if (open) {
            setFullName("");
            setEmail("");
            setRole("operator");
            setAccessKey(generateKey());
            setSubmitting(false);
            setResult(null);
        }
    }, [open]);

    if (!open) return null;

    const canSend = fullName.trim() && emailValid(email) && !submitting && !result;

    const send = async () => {
        if (!canSend) return;
        setSubmitting(true);
        try {
            const data = await inviteOperator(campaignId, {
                full_name: fullName.trim(), email: email.trim(), role, access_key: accessKey,
            });
            setResult(data);
            if (onDone) onDone(data);
        } catch (e) {
            setSubmitting(false);
        }
    };

    const copyKey = () => {
        if (navigator.clipboard) navigator.clipboard.writeText(accessKey);
    };

    const campaignName = campaign?.name;

    return (
        <Backdrop onClick={onCancel}>
            <Card onClick={(e) => e.stopPropagation()}>
                <Header>
                    <HeadText>
                        <Title>{t("words.invite_operator_title")}</Title>
                        <Hint>
                            {campaignName
                                ? t("words.invite_operator_hint", {campaign: campaignName})
                                : t("words.invite_operator_hint_generic")}
                        </Hint>
                    </HeadText>
                    <CloseBtn type="button" onClick={onCancel} aria-label={t("words.cancel")}><RiCloseLine/></CloseBtn>
                </Header>

                <Body>
                    <Label>{t("words.full_name")}</Label>
                    <Input value={fullName} onChange={(e) => setFullName(e.target.value)}
                           placeholder="e.g. Camila Ribeiro" aria-label={t("words.full_name")}/>

                    <Label>{t("words.email")}</Label>
                    <Input value={email} onChange={(e) => setEmail(e.target.value)}
                           placeholder="camila@builderall.com" aria-label={t("words.email")}/>

                    <Label>{t("words.role")}</Label>
                    <Tabs>
                        {ROLES.map((r) => (
                            <Tab key={r} type="button" $active={role === r} onClick={() => setRole(r)}>
                                {t(`words.role_${r}`)}
                            </Tab>
                        ))}
                    </Tabs>
                    <RoleDesc>{t(`words.role_${role}_desc`)}</RoleDesc>

                    <Label>{t("words.access_key")}</Label>
                    <KeyBox>
                        <span>{accessKey}</span>
                        <IconBtn type="button" onClick={copyKey} aria-label={t("words.copy")}><RiFileCopyLine/></IconBtn>
                    </KeyBox>
                    <SubHint>{t("words.access_key_auto_hint")}</SubHint>

                    <AccessTitle
                        dangerouslySetInnerHTML={{
                            __html: t("words.what_role_can_access_html", {role: t(`words.role_${role}`)}),
                        }}
                    />
                    <Checklist>
                        {ACCESS_ITEMS.map((item) => (
                            <CheckItem key={item} $on={ACCESS[role][item]} data-on={ACCESS[role][item] ? "1" : "0"}>
                                {ACCESS[role][item]
                                    ? <RiCheckLine color="#22C55E"/>
                                    : <RiCloseLine color="#EF4444"/>}
                                {t(`words.access_${item}`)}
                            </CheckItem>
                        ))}
                    </Checklist>

                    {result && result.reactivated && <Warn>{t("words.invite_reactivated_warning")}</Warn>}
                    {result && result.emailed === false && <Warn>{t("words.invite_email_failed_warning")}</Warn>}
                    {result && result.emailed && <Success>{t("words.invite_sent_success")}</Success>}
                </Body>

                <Footer>
                    <FooterNote>{t("words.key_expires_note")}</FooterNote>
                    <Actions>
                        <CancelBtn type="button" onClick={onCancel}>
                            {result ? t("words.done") : t("words.cancel")}
                        </CancelBtn>
                        {!result && (
                            <SendBtn type="button" onClick={send} disabled={!canSend}>{t("words.send_invite")}</SendBtn>
                        )}
                    </Actions>
                </Footer>
            </Card>
        </Backdrop>
    );
};

OperatorInviteModal.propTypes = {
    open: PropTypes.bool,
    campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onCancel: PropTypes.func,
    onDone: PropTypes.func,
};

export default OperatorInviteModal;
